/**
 * Helper dùng chung cho trang giới thiệu quán.
 *
 * Tách riêng khỏi component vì định dạng tiền và khung giờ phải giống hệt nhau
 * ở mọi chỗ (hero, bảng giá, thẻ ưu đãi), và vì mấy luật này cần test độc lập
 * được.
 *
 * Ở đây không có business logic của quán: không tính giá, không tính tiền tiết
 * kiệm. Chỉ đọc field backend đã tính sẵn và định dạng lại cho dễ đọc.
 */
import { PromotionSchedule, Promotion, VenueAddress } from "../interfaces/venue";

/* ── Tiền ──────────────────────────────────────────────────────────────── */

/**
 * 269000 -> "269.000đ". Dấu chấm phân cách nghìn, hậu tố đ, không khoảng trắng.
 *
 * Không dùng toCurrency trong helpers/utils.ts: hàm đó phân cách bằng dấu phẩy
 * theo kiểu Anh - Mỹ, còn bảng giá quán phải đọc theo kiểu Việt.
 */
export const formatVnd = (amount: number): string => {
  const rounded = Math.round(Math.abs(amount));
  const sign = amount < 0 ? "-" : "";
  return sign + rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "đ";
};

/** Giá 1 giờ: null nghĩa là admin chưa đặt giá, hiển thị "Liên hệ" chứ không phải 0đ. */
export const formatPricePerHour = (price: number | null): string =>
  price === null || price === undefined ? "Liên hệ" : formatVnd(price);

/* ── Ngày trong tuần & khung giờ ───────────────────────────────────────── */

/** Backend đánh số 2 = thứ Hai … 8 = Chủ nhật (đọc như tên gọi tiếng Việt). */
export const DAY_LABELS: Record<number, string> = {
  2: "T2",
  3: "T3",
  4: "T4",
  5: "T5",
  6: "T6",
  7: "T7",
  8: "CN",
};

/** Đổi Date của JS (0 = Chủ nhật) sang cách đánh số của backend. */
export const dayOfWeekOf = (date: Date): number => {
  const js = date.getDay();
  return js === 0 ? 8 : js + 1;
};

/**
 * [2,3,4,5,6] -> "T2–T6"; [2,4,7] -> "T2, T4, T7".
 *
 * Chỉ gom thành dải khi có từ 3 ngày liên tiếp trở lên — "T2–T3" dài bằng
 * "T2, T3" mà khó đọc hơn.
 */
export const formatDaysOfWeek = (days: number[]): string => {
  const sorted = Array.from(new Set(days)).sort((a, b) => a - b);
  if (sorted.length === 0) {
    return "";
  }

  const parts: string[] = [];
  let runStart = 0;

  for (let i = 1; i <= sorted.length; i += 1) {
    const broken = i === sorted.length || sorted[i] !== sorted[i - 1] + 1;
    if (!broken) {
      continue;
    }

    const length = i - runStart;
    if (length >= 3) {
      parts.push(`${DAY_LABELS[sorted[runStart]]}–${DAY_LABELS[sorted[i - 1]]}`);
    } else {
      for (let j = runStart; j < i; j += 1) {
        parts.push(DAY_LABELS[sorted[j]]);
      }
    }
    runStart = i;
  }

  return parts.join(", ");
};

/** Mảng rỗng hoặc đủ 7 ngày đều nghĩa là "mọi ngày" — không cần nói thêm. */
export const isEveryDay = (days: number[]): boolean => {
  const unique = new Set(days);
  return unique.size === 0 || unique.size >= 7;
};

/**
 * Slot khung giờ của thẻ ưu đãi: "Mọi khung giờ", "00:00 – 08:00",
 * hoặc "08:00 – 12:00 · T2–T6".
 */
export const formatSchedule = (schedule: PromotionSchedule): string => {
  const base =
    schedule.mode === "TIME_RANGE" && schedule.from && schedule.to
      ? `${schedule.from} – ${schedule.to}`
      : "Mọi khung giờ";

  if (isEveryDay(schedule.daysOfWeek)) {
    return base;
  }
  return `${base} · ${formatDaysOfWeek(schedule.daysOfWeek)}`;
};

/** Dải "Tiết kiệm …" chỉ hiện khi backend thực sự gửi số dương. */
export const hasSaving = (promotion: Promotion): boolean =>
  typeof promotion.pricing.savingAmount === "number" &&
  promotion.pricing.savingAmount > 0;

/* ── Địa chỉ & liên hệ ─────────────────────────────────────────────────── */

/**
 * Backend đã ghép sẵn address.full; hàm này chỉ ghép lại khi chuỗi đó trống,
 * để một bản ghi thiếu dữ liệu không làm dòng địa chỉ biến mất.
 */
export const composeAddress = (address: VenueAddress): string => {
  if (address.full && address.full.trim()) {
    return address.full.trim();
  }
  return [
    address.line,
    address.wardName,
    address.districtName,
    address.provinceName,
  ]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");
};

/** Quận/huyện + tỉnh — đủ để biết quán ở đâu mà không tràn dòng trên thẻ. */
export const shortArea = (address: VenueAddress): string =>
  [address.districtName, address.provinceName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");

/** Có toạ độ thì chỉ đường chính xác tới điểm; không thì để Maps tự tìm địa chỉ. */
export const directionsUrl = (
  address: VenueAddress,
  coordinates: { lat: number; lng: number } | null
): string => {
  const destination = coordinates
    ? `${coordinates.lat},${coordinates.lng}`
    : composeAddress(address);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}`;
};

/** Số điện thoại dạng link tel: — bỏ khoảng trắng, dấu chấm, dấu gạch. */
export const telHref = (phone: string): string =>
  `tel:${(phone || "").replace(/[^\d+]/g, "")}`;

/* ── Tiện ích ──────────────────────────────────────────────────────────── */

const AMENITY_ICONS: Record<string, string> = {
  WIFI: "wifi",
  PARKING: "parking",
  FOOD: "tools-kitchen-2",
  DRINK: "cup",
  VIP_ROOM: "door",
  AIR_CONDITIONER: "air-conditioning",
  SMOKING_AREA: "cigarette",
  RESTROOM: "bath",
  LIVESTREAM: "device-tv",
  COACH: "user-star",
  SHOP: "shopping-bag",
  CARD_PAYMENT: "credit-card",
};

/**
 * Admin có thể thêm mã tiện ích mới bất cứ lúc nào. Mã lạ vẫn hiển thị với
 * icon mặc định — bỏ qua nó là làm mất một tiện ích thật của quán.
 */
export const amenityIcon = (code: string): string =>
  AMENITY_ICONS[(code || "").toUpperCase()] || "circle-check";
